/**
 * The virtual account's Categories tab (TMMAPP-0108; audit 06 § A9, the tmm-api review amendments;
 * MM-D-124 / 127; v2 L385–469, reference v2/03). Categories belong to the virtual account; this tab
 * reads `GET …/categories` with every figure served — per category `netMinor`, `lineCount`,
 * `envelopeIds[]` and `unassigned`, and the per-kind group totals — and adds nothing up itself.
 *
 * - Header: "Categories", its sub-line, "{n} categories" and, for writers, "+ ADD CATEGORY" (outlined
 *   with a brand border) → Add category (C8).
 * - Expenses / Income groups (`auto-fit minmax(min(100%, 380px))`): the label, the served total at
 *   20/300, a 12px stacked bar (a title per segment), then the rows, largest first: a 10px swatch,
 *   the name, the tax pill — "Tax deductible" on an expense, "Taxable" on income (MM-D-127), an
 *   `aria-pressed` toggle of `taxReportable` for writers — the line count with the envelopes' dots,
 *   a 6px share bar and the signed amount.
 * - Writers rename inline ("Renamed “A” to “B” everywhere in {VA}." / "“X” already exists.") and
 *   delete with a confirmation in place of the row (TMM-B4): a used category is archived and its
 *   lines keep it; it is no longer offered for new lines.
 * - The Uncategorized footer reads the ledger's served `uncategorized=true` totals and opens the
 *   Ledger filtered to Uncategorized.
 * - A "Totals | P&L" toggle (TMMAPP-0111, D1) swaps the groups for the virtual account P&L by
 *   category and month (`VirtualAccountPnlPanel`).
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  categoriesApi,
  type Category,
  type CategoryFigures,
  type CategoryFiguresList,
  type CategoryKind,
} from '@/api/categories';
import type { Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import { registersApi, type VirtualAccountRegisterTotals } from '@/api/registers';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorState } from '@/components/ErrorState';
import { SegmentedControl } from '@/components/SegmentedControl';
import { AddIcon, DeleteIcon, EditIcon } from '@/components/icons';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { AddCategoryDialog } from './AddCategoryDialog';
import { VirtualAccountPnlPanel } from './VirtualAccountPnlPanel';
import { civilMonthParam, enumParam, useUrlState } from '@/shared/useUrlState';

const CATEGORIES_VIEWS = ['totals', 'pnl'] as const;
type CategoriesView = (typeof CATEGORIES_VIEWS)[number];
const CATEGORIES_URL = {
  view: enumParam<CategoriesView>(CATEGORIES_VIEWS, 'totals'),
  from: civilMonthParam(''),
  to: civilMonthParam(''),
};

const GROUPS: { kind: CategoryKind; label: string }[] = [
  { kind: 'EXPENSE', label: 'virtualAccount.categories.expenses' },
  { kind: 'INCOME', label: 'virtualAccount.categories.income' },
];

interface CategoriesTabProps {
  virtualAccount: VirtualAccountDetail;
  envelopes: Envelope[];
  canWrite: boolean;
  onNotify: (message: string) => void;
  onShowUncategorized: () => void;
  onSubscriptionRequired: () => void;
}

export function CategoriesTab({
  virtualAccount,
  envelopes,
  canWrite,
  onNotify,
  onShowUncategorized,
  onSubscriptionRequired,
}: CategoriesTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [list, setList] = useState<CategoryFiguresList | null>(null);
  const [uncategorized, setUncategorized] = useState<VirtualAccountRegisterTotals | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [adding, setAdding] = useState(false);
  // The view and the P&L's month range live in the URL (TMMAPP-0113 4e).
  const [{ view, from, to }, setUrl] = useUrlState(CATEGORIES_URL);
  const setView = (next: CategoriesView) =>
    setUrl({ view: next, from: '', to: '' }, { push: true });
  const [renaming, setRenaming] = useState<{ id: number; value: string } | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const renameInput = useRef<HTMLInputElement | null>(null);
  const cancelDelete = useRef<HTMLButtonElement | null>(null);
  const renamingId = renaming?.id ?? null;
  // Focus follows the in-row editor: the rename field when it opens, Cancel when a delete asks.
  useEffect(() => {
    if (renamingId !== null) renameInput.current?.focus();
  }, [renamingId]);
  useEffect(() => {
    if (deleting !== null) cancelDelete.current?.focus();
  }, [deleting]);

  const load = useCallback(async () => {
    try {
      const [figures, totals] = await Promise.all([
        categoriesApi.listWithFigures(virtualAccount.id),
        registersApi
          .virtualAccountRegister(virtualAccount.id, { uncategorized: true, limit: 1 })
          .then((page) => page.totals)
          .catch(() => null),
      ]);
      setList(figures);
      setUncategorized(totals);
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, [virtualAccount.id]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const lapsed = (error: unknown) =>
    isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED;

  async function toggleTax(category: CategoryFigures) {
    if (busy) return;
    setBusy(true);
    try {
      await categoriesApi.update(virtualAccount.id, category.id, {
        taxReportable: !category.taxReportable,
      });
      await load();
    } catch (error) {
      if (lapsed(error)) onSubscriptionRequired();
      else onNotify(t('virtualAccount.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  async function saveRename(event: FormEvent<HTMLFormElement>, category: CategoryFigures) {
    event.preventDefault();
    if (renaming === null || busy) return;
    const next = renaming.value.trim();
    if (next.length === 0 || next === category.name) {
      setRenaming(null);
      return;
    }
    const taken = (list?.categories ?? []).some(
      (other) => other.id !== category.id && other.name.toLowerCase() === next.toLowerCase(),
    );
    if (taken) {
      onNotify(t('virtualAccount.categories.exists', { name: next }));
      return;
    }
    setBusy(true);
    try {
      await categoriesApi.update(virtualAccount.id, category.id, { name: next });
      setRenaming(null);
      onNotify(
        t('virtualAccount.categories.renamed', {
          from: category.name,
          to: next,
          name: virtualAccount.name,
        }),
      );
      await load();
    } catch (error) {
      if (lapsed(error)) onSubscriptionRequired();
      else if (isApiError(error) && error.code === 'CATEGORY_NAME_TAKEN') {
        onNotify(t('virtualAccount.categories.exists', { name: next }));
      } else onNotify(t('virtualAccount.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete(category: CategoryFigures) {
    if (busy) return;
    setBusy(true);
    try {
      await categoriesApi.remove(virtualAccount.id, category.id);
      setDeleting(null);
      onNotify(t('virtualAccount.categories.deleted', { name: category.name }));
      await load();
    } catch (error) {
      if (lapsed(error)) onSubscriptionRequired();
      else onNotify(t('virtualAccount.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  if (failure !== null && list === null) {
    return (
      <ErrorState
        variant="section"
        error={failure.error}
        what={t('errors.what.categories')}
        onRetry={() => void load()}
      />
    );
  }

  const active = (list?.categories ?? []).filter((category) => category.archivedAt === null);
  const envelopeName = (id: number) => envelopes.find((envelope) => envelope.id === id)?.name ?? '';
  const signedMoney = (minor: number) =>
    formatMoneyMinor(minor, virtualAccount.currency, locale, true);
  const label = tokens.textLabel('surface');

  function row(category: CategoryFigures, max: number) {
    const net = category.netMinor;
    const swatch = categoricalFor(category.id, tokens).fill;
    const taxLabel = t(`virtualAccount.tax.${category.kind}`);
    if (deleting === category.id) {
      return (
        <Box
          key={category.id}
          role="group"
          aria-label={t('virtualAccount.categories.deleteTitle', { name: category.name })}
          data-category-delete=""
          sx={{ py: 1, borderTop: `1px solid ${tokens.line}` }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {t('virtualAccount.categories.deleteTitle', { name: category.name })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.text2, mt: '2px' }}>
            {t('virtualAccount.categories.deleteBody')}
          </Typography>
          <Stack direction="row" sx={{ gap: 1, mt: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              ref={cancelDelete}
              disabled={busy}
              onClick={() => {
                setDeleting(null);
                opener.current?.focus();
              }}
            >
              {t('common.cancel')}
            </Button>
            <SubmitButton
              size="small"
              color="error"
              loading={busy}
              onClick={() => void confirmDelete(category)}
            >
              {t('virtualAccount.categories.delete')}
            </SubmitButton>
          </Stack>
        </Box>
      );
    }
    return (
      <Box
        key={category.id}
        data-category-row={category.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: canWrite ? 'minmax(0, 1fr) 96px 56px' : 'minmax(0, 1fr) 96px',
          gap: '10px',
          alignItems: 'center',
          py: 1,
          borderTop: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              flex: '0 0 10px',
              width: 10,
              height: 10,
              borderRadius: '2px',
              bgcolor: swatch,
              mt: '4px',
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {renaming?.id === category.id ? (
              <Box
                component="form"
                onSubmit={(event: FormEvent<HTMLFormElement>) => void saveRename(event, category)}
                sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}
              >
                <TextField
                  size="small"
                  value={renaming.value}
                  onChange={(event) => setRenaming({ id: category.id, value: event.target.value })}
                  inputRef={renameInput}
                  slotProps={{
                    htmlInput: {
                      'aria-label': t('virtualAccount.categories.renameLabel', {
                        name: category.name,
                      }),
                      maxLength: 80,
                    },
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.brandBoundary },
                  }}
                />
                <SubmitButton size="small" type="submit" loading={busy}>
                  {t('common.save')}
                </SubmitButton>
                <Button size="small" disabled={busy} onClick={() => setRenaming(null)}>
                  {t('common.cancel')}
                </Button>
              </Box>
            ) : (
              <>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
                    {category.name}
                  </Typography>
                  {canWrite ? (
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={category.taxReportable}
                      aria-label={t('virtualAccount.categories.taxToggle', {
                        tax: taxLabel,
                        name: category.name,
                      })}
                      data-tax-pill={category.taxReportable ? 'on' : 'off'}
                      disabled={busy}
                      onClick={() => void toggleTax(category)}
                      sx={taxPillSx(tokens, category.taxReportable, true)}
                    >
                      {taxLabel}
                    </Box>
                  ) : (
                    category.taxReportable && (
                      <Box component="span" data-tax-pill="on" sx={taxPillSx(tokens, true, false)}>
                        {taxLabel}
                      </Box>
                    )
                  )}
                </Stack>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    fontSize: 11,
                    color: label,
                  }}
                >
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                    {t('virtualAccount.ledger.count', { count: category.lineCount })}
                  </Box>
                  {category.unassigned && (
                    <Box
                      component="span"
                      title={t('virtualAccount.unassigned')}
                      aria-hidden
                      sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.text3 }}
                    />
                  )}
                  {category.envelopeIds.map((id) => (
                    <Box
                      key={id}
                      component="span"
                      title={envelopeName(id)}
                      aria-hidden
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: categoricalFor(id, tokens).line,
                      }}
                    />
                  ))}
                </Stack>
                <Box
                  aria-hidden
                  sx={{
                    height: 6,
                    bgcolor: tokens.hover,
                    borderRadius: '1px',
                    position: 'relative',
                    mt: '6px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      insetInlineStart: 0,
                      top: 0,
                      bottom: 0,
                      width: `${max === 0 ? 0 : (Math.abs(net) / max) * 100}%`,
                      bgcolor: swatch,
                      borderRadius: '1px',
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'end' }}>
          <MoneyText
            minor={category.netMinor}
            currency={virtualAccount.currency}
            tone="register"
            variant="body2"
          />
        </Box>
        {canWrite && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: '2px' }}>
            <IconButton
              size="small"
              aria-label={t('virtualAccount.categories.rename', { name: category.name })}
              disabled={busy}
              onClick={() => setRenaming({ id: category.id, value: category.name })}
            >
              <EditIcon size={14} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t('virtualAccount.categories.deleteItem', { name: category.name })}
              disabled={busy}
              onClick={(event) => {
                opener.current = event.currentTarget;
                setDeleting(category.id);
              }}
            >
              <DeleteIcon size={14} />
            </IconButton>
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Paper component="section" aria-labelledby="categories-title" sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          p: '16px 20px 12px',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Typography id="categories-title" variant="h3" component="h2">
            {t('virtualAccount.categories.title')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('virtualAccount.categories.sub', { name: virtualAccount.name })}
          </Typography>
        </Box>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SegmentedControl<'totals' | 'pnl'>
            filter
            value={view}
            onChange={setView}
            aria-label={t('pnl.viewLabel')}
            options={[
              { value: 'totals', label: t('pnl.viewTotals') },
              { value: 'pnl', label: t('pnl.viewPnl') },
            ]}
          />
          <Typography variant="caption" sx={{ color: label }}>
            {list === null ? '' : t('envelope.categoryCount', { count: active.length })}
          </Typography>
          {canWrite && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              sx={{ borderColor: tokens.brandBoundary }}
            >
              {t('virtualAccount.addCategory.title')}
            </Button>
          )}
        </Stack>
      </Stack>
      {view === 'pnl' ? (
        <VirtualAccountPnlPanel
          virtualAccountId={virtualAccount.id}
          currency={virtualAccount.currency}
          months={{ from, to }}
          onMonths={(next) => setUrl(next, { push: true })}
        />
      ) : list === null ? (
        <Box sx={{ p: '0 20px 16px' }} aria-busy="true">
          <Skeleton variant="rounded" height={160} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
            borderTop: `1px solid ${tokens.line}`,
            mb: '-1px',
          }}
        >
          {GROUPS.map((group) => {
            const rows = active
              .filter((category) => category.kind === group.kind)
              .sort((a, b) => Math.abs(b.netMinor) - Math.abs(a.netMinor));
            const max = Math.max(0, ...rows.map((category) => Math.abs(category.netMinor)));
            const sum = rows.reduce((acc, category) => acc + Math.abs(category.netMinor), 0);
            const total = group.kind === 'EXPENSE' ? list.expenseNetMinor : list.incomeNetMinor;
            return (
              <Box
                key={group.kind}
                component="section"
                aria-labelledby={`categories-${group.kind}`}
                data-category-group={group.kind}
                sx={{
                  p: '16px 20px 18px',
                  borderInlineEnd: `1px solid ${tokens.line}`,
                  borderBottom: `1px solid ${tokens.line}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}
                >
                  <Typography
                    id={`categories-${group.kind}`}
                    component="h3"
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: tokens.text2,
                    }}
                  >
                    {t(group.label)}
                  </Typography>
                  <Typography
                    data-group-total=""
                    sx={{ fontSize: 20, fontWeight: 300, fontVariantNumeric: 'tabular-nums' }}
                  >
                    <MoneyParts minor={total} currency={virtualAccount.currency} />
                  </Typography>
                </Stack>
                <Box
                  aria-hidden
                  data-stacked-bar=""
                  sx={{
                    display: 'flex',
                    height: 12,
                    bgcolor: tokens.hover,
                    borderRadius: '2px',
                    overflow: 'hidden',
                    gap: '1px',
                  }}
                >
                  {rows
                    .filter((category) => category.netMinor !== 0)
                    .map((category) => (
                      <Box
                        key={category.id}
                        title={`${category.name} · ${signedMoney(category.netMinor)}`}
                        sx={{
                          width: `${sum === 0 ? 0 : (Math.abs(category.netMinor) / sum) * 100}%`,
                          bgcolor: categoricalFor(category.id, tokens).fill,
                        }}
                      />
                    ))}
                </Box>
                <Box>
                  {rows.map((category) => row(category, max))}
                  {rows.length === 0 && (
                    <Typography
                      sx={{
                        py: 1.25,
                        borderTop: `1px solid ${tokens.line}`,
                        fontSize: 13,
                        color: label,
                      }}
                    >
                      {t('virtualAccount.categories.none')}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {view === 'totals' && uncategorized !== null && (uncategorized.count ?? 0) > 0 && (
        <Stack
          direction="row"
          data-uncategorized-footer=""
          sx={{
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            p: '12px 20px',
            borderTop: `1px solid ${tokens.line}`,
            fontSize: 13,
          }}
        >
          <Box
            component="span"
            sx={{
              border: `1px dashed ${tokens.text3}`,
              borderRadius: '3px',
              p: '1px 7px',
              fontSize: 11,
              color: tokens.text2,
            }}
          >
            {t('envelope.uncategorized')}
          </Box>
          <MoneyText
            minor={uncategorized.netMinor}
            currency={virtualAccount.currency}
            variant="body2"
            signed
          />
          <Box component="span" sx={{ color: label }}>
            {t('virtualAccount.ledger.count', { count: uncategorized.count ?? 0 })}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Link
            component="button"
            type="button"
            onClick={onShowUncategorized}
            underline="none"
            // A link reads as its catalog copy, sentence case (visual contract v2 §7, TMMAPP-0155 item 9).
            sx={{ fontSize: 12, fontWeight: 500 }}
          >
            {t('virtualAccount.categories.showInLedger')}
          </Link>
        </Stack>
      )}
      {adding && (
        <AddCategoryDialog
          virtualAccountId={virtualAccount.id}
          onClose={() => setAdding(false)}
          onAdded={async (category: Category) => {
            onNotify(t('virtualAccount.categories.added', { name: category.name }));
            await load();
          }}
          onSubscriptionRequired={onSubscriptionRequired}
        />
      )}
    </Paper>
  );
}

function taxPillSx(tokens: ReturnType<typeof resolveTokens>, on: boolean, control: boolean) {
  return {
    display: 'inline-block',
    p: '0 6px',
    border: `1px solid ${on ? 'transparent' : tokens.line}`,
    borderRadius: '3px',
    // On: `link` over the `selected` layer on an opaque `surface` (a guaranteed pair).
    backgroundColor: on ? tokens.surface : 'transparent',
    backgroundImage: on ? `linear-gradient(${tokens.selected}, ${tokens.selected})` : 'none',
    color: on ? tokens.link : tokens.textLabel('surface'),
    fontFamily: 'inherit',
    fontSize: 10,
    lineHeight: '16px',
    fontWeight: 500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    cursor: control ? 'pointer' : 'default',
    '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
  };
}
